import React, { useMemo } from 'react';
import { useChainContext } from '../contexts/ChainContext';
import { useBlockchainService } from '../contexts/BlockchainServiceContext';
import useMonitorData from '../hooks/useMonitorData';
import useProducerSchedule from '../hooks/useProducerSchedule';
import SearchBand from '../components/SearchBand';
import { apiLag } from '../utils/chainClock';
import {
  LoadingState,
  ErrorState,
  ApiStatusIndicator,
  NetworkStats,
  LiveLatestBlocks,
  TopProducers
} from '../components/Monitor';

/**
 * Portada (estilo "Classic Explorer"): buscador grande, estadísticas de red,
 * últimos bloques y top productores con el schedule en vivo.
 *
 * Head, LIB, productor actual y últimos bloques se calculan con el último
 * get_info y el schedule (utils/chainClock): sin peticiones extra a la API.
 */
// "TELOS" → "Telos"; siglas cortas como "XPR" se quedan igual
const displayChainName = (name = '') =>
  name.length > 3 && name === name.toUpperCase() ? name[0] + name.slice(1).toLowerCase() : name;

function Monitor() {
  const { currentChain } = useChainContext();
  const { getService } = useBlockchainService();

  const blockchainService = useMemo(() => {
    return currentChain ? getService(currentChain) : null;
  }, [currentChain, getService]);

  const {
    producers,
    blockchainInfo,
    loading,
    error,
    currentPrice,
    chainConfig,
    apiStatus,
    retryCount,
    retryFetch
  } = useMonitorData(currentChain);

  const schedule = useProducerSchedule(blockchainService, { measureLag: false });
  const lagMs = apiLag(blockchainInfo) ?? 0;
  const symbol = blockchainService?.getChainSymbol() || '';

  let body;
  if (loading) {
    body = <LoadingState />;
  } else if (error && producers.length === 0) {
    // Error fatal sin datos → estado de error con retry
    body = (
      <div className="card">
        <ErrorState error={error} onRetry={retryFetch} chainName={chainConfig?.name} title="Couldn't load blockchain data" />
      </div>
    );
  } else {
    body = (
      <>
        {/* Error no fatal con datos antiguos → aviso no bloqueante */}
        {error && (
          <ApiStatusIndicator
            apiStatus={apiStatus}
            error={error}
            retryCount={retryCount}
            onRetry={retryFetch}
            chainName={chainConfig?.name}
          />
        )}

        <NetworkStats
          blockchainInfo={blockchainInfo}
          active={schedule.active}
          currentPrice={currentPrice}
          symbol={symbol}
          blockchainService={blockchainService}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LiveLatestBlocks blockchainInfo={blockchainInfo} active={schedule.active} />
          <TopProducers
            producers={producers}
            active={schedule.active}
            lagMs={lagMs}
            chainFamily={chainConfig?.chainFamily}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <SearchBand hero title={`The ${displayChainName(chainConfig?.name)} Blockchain Explorer`} />
      <div className="page-container -mt-16 relative flex flex-col gap-6">
        {body}
      </div>
    </>
  );
}

export default React.memo(Monitor);
